import { Alert } from 'src/design/atoms/Alert';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { EmptyState } from 'src/design/atoms/EmptyState';
import { Group } from 'src/design/atoms/Group';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconRefresh,
} from '@tabler/icons-react';
import React, { useMemo, useState } from 'react';
import { getErrorMessage } from 'src/utils';
import { Header } from 'src/design/organisms/Header';
import { INSPECTION_STATE_META } from 'src/app/inspections/inspectionStates';
import {
  InspectionState,
  useInspectionsOverview,
} from 'src/api/queries/inspections/useInspectionsOverview';
import { WorkflowStatusPanel } from 'src/design/molecules/WorkflowStatusPanel';
import { InspectionsOverviewTable } from './components/InspectionsOverviewTable';

const ALL_STATES = 'All';

export const Inspections: React.FC = () => {
  const { data, isLoading, isFetching, error, refetch } =
    useInspectionsOverview();

  const linked = data?.linked ?? false;
  const inspections = useMemo(() => data?.inspections ?? [], [data]);
  const sourceUnavailable = data?.sourceUnavailable ?? false;

  const [stateFilter, setStateFilter] = useState(ALL_STATES);

  const segments = useMemo(() => {
    const counts = new Map<InspectionState, number>();
    for (const inspection of inspections) {
      counts.set(inspection.state, (counts.get(inspection.state) ?? 0) + 1);
    }
    return [
      {
        value: ALL_STATES,
        label: ALL_STATES,
        count: inspections.length,
        color: 'gray',
      },
      ...INSPECTION_STATE_META.map((meta) => ({
        value: String(meta.state),
        label: meta.label,
        count: counts.get(meta.state) ?? 0,
        color: meta.color,
      })),
    ];
  }, [inspections]);

  const filteredInspections = useMemo(
    () =>
      stateFilter === ALL_STATES
        ? inspections
        : inspections.filter(
            (inspection) => String(inspection.state) === stateFilter,
          ),
    [inspections, stateFilter],
  );

  const refreshButton = (
    <Group justify="center" mt="md">
      <BasicButton
        onClick={() => void refetch()}
        loading={isFetching}
        leftSection={<IconRefresh size={16} />}
        variant="light"
        size="sm"
      >
        Refresh
      </BasicButton>
    </Group>
  );

  return (
    <>
      <Header
        title="Inspections"
        breadcrumbProps={{ items: [{ text: 'Inspections' }] }}
        helpText="View the inspections assigned to you in Proact and open one to perform it."
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          mb="md"
        >
          {getErrorMessage(error)}
        </Alert>
      )}

      {sourceUnavailable && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Proact is unavailable"
          color="yellow"
          mb="md"
        >
          Inspections could not be loaded from Proact. Try again later.
        </Alert>
      )}

      {error ? (
        refreshButton
      ) : !isLoading && !linked ? (
        <>
          <EmptyState
            header="Not linked"
            description="Your account is not linked to Proact yet"
          />
          {refreshButton}
        </>
      ) : !isLoading && inspections.length === 0 ? (
        <>
          <EmptyState
            header="No inspections"
            description="You have no inspections assigned in Proact."
          />
          {refreshButton}
        </>
      ) : (
        <>
          <WorkflowStatusPanel
            segments={segments}
            value={stateFilter}
            onChange={setStateFilter}
            allValue={ALL_STATES}
            ariaLabel="Filter by inspection state"
          />

          <InspectionsOverviewTable
            inspections={filteredInspections}
            loading={isLoading}
            fetching={isFetching}
            onRefresh={() => void refetch()}
          />
        </>
      )}
    </>
  );
};
